<template>
  <div class="container">
    <!-- 面包屑导航 -->
    <el-breadcrumb separator="/">
      <el-breadcrumb-item :to="{ path: '/welcome' }">首页</el-breadcrumb-item>
      <el-breadcrumb-item>财务管理</el-breadcrumb-item>
      <el-breadcrumb-item>运营数据</el-breadcrumb-item>
    </el-breadcrumb>

    <!-- 主卡：三 Tab（项目经营汇总 / 门店收入明细 / 品牌月度汇总） -->
    <el-card class="main-card">
      <div slot="header" class="toolbar">
        <span class="card-header">
          运营数据
          <span class="header-summary">三平台经营收入 · 应抽金额计算 · 门店明细核对（抽点品牌对账底稿）</span>
        </span>
        <span class="toolbar-tip">应抽金额 =（美团外卖 + 淘宝闪购 + 京东）营业额 × 合同抽佣比例 · 拼好饭/爆品团已含在平台营业额内不重复计</span>
      </div>

      <el-tabs v-model="activeTab">
        <!-- Tab1 项目经营汇总 -->
        <el-tab-pane label="项目经营汇总" name="project">
          <!-- 指标卡：4 张（随筛选联动，全量口径） -->
          <div class="stat-cards">
            <div class="stat-card">
              <div class="stat-card-num">{{ fmtMoney(project.stats.totalAmount) }}</div>
              <div class="stat-card-label">总营业额（元）</div>
            </div>
            <div class="stat-card">
              <div class="stat-card-num">{{ fmtMoney(project.stats.dueAmount) }}</div>
              <div class="stat-card-label">应抽金额合计（元）</div>
            </div>
            <div class="stat-card">
              <div class="stat-card-num">{{ project.stats.projectCount }}</div>
              <div class="stat-card-label">在统计项目数</div>
            </div>
            <div class="stat-card" :class="{ warn: project.stats.missingCount > 0 }">
              <div class="stat-card-num">{{ project.stats.missingCount }}</div>
              <div class="stat-card-label">缺数项目数</div>
            </div>
          </div>

          <!-- 筛选行 -->
          <div class="filter-row">
            <el-date-picker
              v-model="project.dateRange"
              type="daterange"
              value-format="yyyy-MM-dd"
              range-separator="至"
              start-placeholder="账期开始日期"
              end-placeholder="账期结束日期"
              size="small"
              style="width: 250px;"
              :clearable="false"
              @change="onProjectFilterChange"
            ></el-date-picker>
            <el-select v-model="project.brand" placeholder="品牌" clearable size="small" style="width: 120px;" @change="onProjectFilterChange">
              <el-option v-for="item in brandOptions" :key="item" :label="item" :value="item"></el-option>
            </el-select>
            <el-select v-model="project.signerId" placeholder="签约人" clearable filterable size="small" style="width: 110px;" @change="onProjectFilterChange">
              <el-option v-for="item in signers" :key="item._id" :label="item.name" :value="item._id"></el-option>
            </el-select>
            <el-input v-model="project.keyword" placeholder="项目/客户" clearable size="small" style="width: 140px;" @keyup.enter.native="onProjectFilterChange" @clear="onProjectFilterChange"></el-input>
            <el-button size="small" @click="resetProjectFilter">重置</el-button>
            <el-button size="small" icon="el-icon-download" :loading="project.exporting" @click="handleExport">导出</el-button>
            <span class="toolbar-tip">账期默认当月 · 跨度上限 92 天 · 导出所见即所得（跟随筛选）</span>
          </div>

          <el-table v-loading="project.loading" :data="project.list" border stripe size="small" max-height="520">
            <el-table-column type="expand">
              <template slot-scope="scope">
                <div class="expand-body">
                  <div class="expand-title">应抽金额计算依据（{{ scope.row.projectName }}）</div>
                  <div class="expand-line" v-if="scope.row.contract">依据合同：{{ scope.row.contract.contractNo || '无编号' }}（{{ scope.row.contract.contractType === 2 ? '续签' : '新签' }}，签约日 {{ scope.row.contract.signDate || '—' }}，服务期 {{ scope.row.contract.serviceStart || '—' }} ~ {{ scope.row.contract.serviceEnd || '—' }}）</div>
                  <div class="expand-line" v-else>该项目无有效合同（合同可能未录入或已删除），无法计算应抽金额</div>
                  <div class="expand-line" v-if="scope.row.commissionRate">抽佣比例：{{ scope.row.commissionRate }}%</div>
                  <div class="expand-line">基数构成：美团 {{ fmtMoney(scope.row.mtAmount) }} + 淘宝 {{ fmtMoney(scope.row.tbAmount) }} + 京东 {{ fmtMoney(scope.row.jdAmount) }} = <b>{{ fmtMoney(scope.row.totalAmount) }}</b>（仅统计合同服务期内的账期区间）</div>
                  <div class="expand-line" v-if="scope.row.dueAmount !== null && scope.row.dueAmount !== undefined">应抽金额 = {{ fmtMoney(scope.row.totalAmount) }} × {{ scope.row.commissionRate }}% = <b class="due-text">{{ fmtMoney(scope.row.dueAmount) }}</b></div>
                  <div class="expand-line warn-line" v-if="isMissing(scope.row)">数据完整性：该账期应采 {{ scope.row.totalDays }} 天、实采 {{ scope.row.collectedDays }} 天，缺 {{ scope.row.totalDays - scope.row.collectedDays }} 天数据，应抽金额仅供参考，补采完成后再对账</div>
                  <div class="expand-line warn-line" v-if="scope.row.rateChanged">比例变更：{{ scope.row.rateNote }}</div>
                  <div class="expand-line warn-line" v-if="scope.row.terminated">终止说明：{{ scope.row.rescindedDate ? `该项目已于 ${scope.row.rescindedDate} 解约，营业额仅统计至解约日，供历史账期核对` : '该项目为解约/退款终止态，营业额仅统计合同服务期内' }}</div>
                </div>
              </template>
            </el-table-column>
            <el-table-column label="#" type="index" width="55" :index="i => (project.pagenum - 1) * project.pagesize + i + 1"></el-table-column>
            <el-table-column label="项目" min-width="170" show-overflow-tooltip>
              <template slot-scope="scope">
                <span>{{ scope.row.projectName }}</span>
                <el-tag v-if="scope.row.terminated" type="danger" size="mini" class="row-tag">已解约</el-tag>
                <el-tag v-if="scope.row.rateChanged" type="warning" size="mini" class="row-tag">比例变更</el-tag>
                <el-tag v-if="isMissing(scope.row)" type="warning" size="mini" class="row-tag">缺数</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="客户" prop="customerName" width="90" show-overflow-tooltip></el-table-column>
            <el-table-column label="品牌" prop="brand" width="95" align="center"></el-table-column>
            <el-table-column label="签约人" prop="signerName" width="75" align="center"></el-table-column>
            <el-table-column label="门店数" prop="storeCount" width="65" align="center"></el-table-column>
            <el-table-column label="美团营业额（元）" min-width="124" align="right">
              <template slot-scope="scope">{{ fmtMoney(scope.row.mtAmount) }}</template>
            </el-table-column>
            <el-table-column label="淘宝营业额（元）" min-width="124" align="right">
              <template slot-scope="scope">{{ fmtMoney(scope.row.tbAmount) }}</template>
            </el-table-column>
            <el-table-column label="京东营业额（元）" min-width="124" align="right">
              <template slot-scope="scope">{{ fmtMoney(scope.row.jdAmount) }}</template>
            </el-table-column>
            <el-table-column label="合计营业额（元）" min-width="130" align="right">
              <template slot-scope="scope">
                <span class="money-text">{{ fmtMoney(scope.row.totalAmount) }}</span>
              </template>
            </el-table-column>
            <el-table-column label="抽佣比例" width="80" align="center">
              <template slot-scope="scope">{{ scope.row.commissionRate ? scope.row.commissionRate + '%' : '—' }}</template>
            </el-table-column>
            <el-table-column label="应抽金额（元）" width="110" align="right">
              <template slot-scope="scope">
                <span v-if="scope.row.dueAmount !== null && scope.row.dueAmount !== undefined" class="due-text">{{ fmtMoney(scope.row.dueAmount) }}</span>
                <span v-else class="muted-text">不适用</span>
              </template>
            </el-table-column>
            <el-table-column label="数据完整性" width="95" align="center">
              <template slot-scope="scope">
                <span v-if="isMissing(scope.row)" class="money-refund">{{ scope.row.collectedDays }}/{{ scope.row.totalDays }} 天</span>
                <span v-else class="ok-text">{{ scope.row.collectedDays }}/{{ scope.row.totalDays }} 天</span>
              </template>
            </el-table-column>
          </el-table>
          <el-pagination
            class="pager"
            @size-change="s => { project.pagesize = s; project.pagenum = 1; fetchProject() }"
            @current-change="p => { project.pagenum = p; fetchProject() }"
            :current-page="project.pagenum"
            :page-sizes="[10, 20, 50]"
            :page-size="project.pagesize"
            layout="total, sizes, prev, pager, next, jumper"
            :total="project.total">
          </el-pagination>
          <div class="foot-tip">口径：应抽金额按项目最新有效合同抽佣比例计算；期间内比例有变更的项目已标注，需按合同分段核对；服务期外与解约日后的营业额不计入</div>
        </el-tab-pane>

        <!-- Tab2 门店收入明细 -->
        <el-tab-pane label="门店收入明细" name="store">
          <!-- 筛选行 -->
          <div class="filter-row">
            <el-date-picker
              v-model="store.dateRange"
              type="daterange"
              value-format="yyyy-MM-dd"
              range-separator="至"
              start-placeholder="账期开始日期"
              end-placeholder="账期结束日期"
              size="small"
              style="width: 250px;"
              :clearable="false"
              @change="onStoreFilterChange"
            ></el-date-picker>
            <el-input v-model="store.keyword" placeholder="项目/店铺" clearable size="small" style="width: 150px;" @keyup.enter.native="onStoreFilterChange" @clear="onStoreFilterChange"></el-input>
            <el-select v-model="store.platform" placeholder="平台" clearable size="small" style="width: 120px;" @change="onStoreFilterChange">
              <el-option v-for="(item, key) in platformMap" :key="key" :label="item.label" :value="key"></el-option>
            </el-select>
            <el-select v-model="store.brand" placeholder="品牌" clearable size="small" style="width: 120px;" @change="onStoreFilterChange">
              <el-option v-for="item in brandOptions" :key="item" :label="item" :value="item"></el-option>
            </el-select>
            <el-button size="small" @click="resetStoreFilter">重置</el-button>
            <span class="toolbar-tip">与项目汇总同口径（按所属项目服务期截断），数字可对平</span>
          </div>

          <el-table v-loading="store.loading" :data="store.list" border stripe size="small" max-height="520">
            <el-table-column label="#" type="index" width="45" :index="i => (store.pagenum - 1) * store.pagesize + i + 1"></el-table-column>
            <el-table-column label="所属项目" prop="projectName" min-width="160" show-overflow-tooltip></el-table-column>
            <el-table-column label="品牌" prop="brand" width="95" align="center"></el-table-column>
            <el-table-column label="平台" width="95" align="center">
              <template slot-scope="scope">
                <el-tag :type="platformMap[scope.row.platform] && platformMap[scope.row.platform].type" size="mini">{{ platformMap[scope.row.platform] ? platformMap[scope.row.platform].label : scope.row.platform }}</el-tag>
              </template>
            </el-table-column>
            <el-table-column label="店铺名称" prop="storeName" min-width="200" show-overflow-tooltip></el-table-column>
            <el-table-column label="营业额（元）" width="115" align="right">
              <template slot-scope="scope">
                <span class="money-text">{{ fmtMoney(scope.row.amount) }}</span>
              </template>
            </el-table-column>
            <el-table-column label="有效订单" prop="orders" width="90" align="center"></el-table-column>
            <el-table-column label="客单价（元）" width="100" align="right">
              <template slot-scope="scope">{{ fmtMoney(scope.row.avgPrice) }}</template>
            </el-table-column>
            <el-table-column label="数据天数" width="95" align="center">
              <template slot-scope="scope">
                <span v-if="scope.row.days < scope.row.totalDays" class="money-refund">{{ scope.row.days }}/{{ scope.row.totalDays }} 天</span>
                <span v-else class="ok-text">{{ scope.row.days }}/{{ scope.row.totalDays }} 天</span>
              </template>
            </el-table-column>
          </el-table>
          <el-pagination
            class="pager"
            @size-change="s => { store.pagesize = s; store.pagenum = 1; fetchStore() }"
            @current-change="p => { store.pagenum = p; fetchStore() }"
            :current-page="store.pagenum"
            :page-sizes="[10, 20, 50]"
            :page-size="store.pagesize"
            layout="total, sizes, prev, pager, next, jumper"
            :total="store.total">
          </el-pagination>
        </el-tab-pane>

        <!-- Tab3 品牌月度汇总 -->
        <el-tab-pane label="品牌月度汇总" name="brand">
          <!-- 筛选行 -->
          <div class="filter-row">
            <el-date-picker
              v-model="brand.dateRange"
              type="daterange"
              value-format="yyyy-MM-dd"
              range-separator="至"
              start-placeholder="开始日期"
              end-placeholder="结束日期"
              size="small"
              style="width: 250px;"
              :clearable="false"
              @change="fetchBrandMonthly"
            ></el-date-picker>
            <span class="toolbar-tip">按自然月汇总 · 跨度上限 186 天（约 6 个月） · 应抽按项目逐个计算后汇总</span>
          </div>

          <el-table v-loading="brand.loading" :data="brand.list" border stripe size="small" show-summary :summary-method="brandSummary">
            <el-table-column label="品牌" prop="brand" width="120" align="center"></el-table-column>
            <el-table-column
              v-for="(m, mi) in brand.months"
              :key="m"
              :label="m + ' 营业额（元）'"
              min-width="165"
              align="right">
              <template slot-scope="scope">{{ fmtMoney(scope.row.months[mi] && scope.row.months[mi].amount) }}</template>
            </el-table-column>
            <el-table-column label="区间合计（元）" min-width="160" align="right">
              <template slot-scope="scope">
                <span class="money-text">{{ fmtMoney(scope.row.totalAmount) }}</span>
              </template>
            </el-table-column>
            <el-table-column label="应抽金额合计（元）" min-width="140" align="right">
              <template slot-scope="scope">
                <span class="due-text">{{ fmtMoney(scope.row.totalDueAmount) }}</span>
              </template>
            </el-table-column>
          </el-table>
          <div class="foot-tip">口径：品牌内各项目分别按其合同抽佣比例计算后汇总（比例不同的项目自动分列）；营业额仅统计各项目服务期内数据</div>
        </el-tab-pane>
      </el-tabs>
    </el-card>
  </div>
</template>

<script>
export default {
  name: 'FinanceYunyingData',
  data () {
    return {
      activeTab: 'project',
      // 品牌选项（仅四品牌抽点项目；代运营收费在招商数据栏目体现，不纳入本栏目）
      brandOptions: ['美滋滋', '蛮碗香', '虎头张', '长安大东家'],
      platformMap: {
        meituan: { label: '美团外卖', type: 'warning' },
        taobao: { label: '淘宝闪购', type: 'danger' },
        jd: { label: '京东', type: 'primary' }
      },

      // 签约人下拉（招商部门前缀在职员工）
      signers: [],

      // 各 Tab 状态（服务端分页；筛选状态独立保留）
      project: {
        dateRange: [],
        brand: '',
        signerId: '',
        keyword: '',
        pagenum: 1,
        pagesize: 20,
        list: [],
        total: 0,
        loading: false,
        exporting: false,
        stats: { totalAmount: 0, dueAmount: 0, projectCount: 0, missingCount: 0 }
      },
      store: {
        dateRange: [],
        keyword: '',
        platform: '',
        brand: '',
        pagenum: 1,
        pagesize: 20,
        list: [],
        total: 0,
        loading: false
      },
      brand: {
        dateRange: [],
        list: [],
        months: [],
        loading: false
      }
    }
  },
  created () {
    // 三个 Tab 查询区间默认当月（本月第一天 ~ 今天）
    const defaultRange = this._defaultMonthRange()
    this.project.dateRange = defaultRange
    this.store.dateRange = [...defaultRange]
    this.brand.dateRange = [...defaultRange]
    this.loadOptions()
    this.fetchProject()
    this.fetchStore()
    this.fetchBrandMonthly()
  },
  methods: {
    /**
     * 默认查询区间：本月第一天 ~ 今天（YYYY-MM-DD）
     */
    _defaultMonthRange () {
      const fmtDay = d => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
      const now = new Date()
      return [fmtDay(new Date(now.getFullYear(), now.getMonth(), 1)), fmtDay(now)]
    },
    /**
     * 金额格式化：两位小数 + 千分位
     */
    fmtMoney (v) {
      if (v === null || v === undefined || isNaN(v)) return '—'
      return Number(v).toLocaleString('zh-CN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    /**
     * 缺数判定：实采天数 < 应采天数
     */
    isMissing (row) {
      return row.collectedDays < row.totalDays
    },
    /**
     * 组装账期区间参数（列表与导出共用，保证所见即所得）
     */
    _spanParams (range) {
      const [startDate, endDate] = range || []
      const params = {}
      if (startDate) params.startDate = startDate
      if (endDate) params.endDate = endDate
      return params
    },
    /**
     * 筛选日期段拼文件名用
     */
    _dateTag (range) {
      const [start, end] = range || []
      if (start && end) return start === end ? String(start) : `${start}_${end}`
      return '全部'
    },

    /* ===== Tab1 项目经营汇总 ===== */
    onProjectFilterChange () {
      // 筛选变更回到第一页，避免旧页码超出新结果集导致空列表
      this.project.pagenum = 1
      this.fetchProject()
    },
    async fetchProject () {
      this.project.loading = true
      try {
        const params = {
          pagenum: this.project.pagenum,
          pagesize: this.project.pagesize,
          ...this._spanParams(this.project.dateRange)
        }
        if (this.project.brand) params.brand = this.project.brand
        if (this.project.signerId) params.signerId = this.project.signerId
        if (this.project.keyword.trim()) params.keyword = this.project.keyword.trim()
        const { data: res } = await this.$http.get('finance/yunyingData/project/list', { params })
        if (res.meta.status !== 200) return this.$message.error(res.meta.msg)
        this.project.list = res.data.list || []
        this.project.total = res.data.total || 0
        this.project.stats = res.data.stats || this.project.stats
        if (res.data.stats && res.data.stats.truncated) this.$message.warning('项目数量超过 20000 上限，数据已截断，指标仅供参考')
      } catch (error) {
        this.$message.error('获取项目经营汇总失败')
      } finally {
        this.project.loading = false
      }
    },
    resetProjectFilter () {
      this.project = {
        dateRange: this._defaultMonthRange(),
        brand: '',
        signerId: '',
        keyword: '',
        pagenum: 1,
        pagesize: 20,
        list: [],
        total: 0,
        loading: false,
        exporting: false,
        stats: { totalAmount: 0, dueAmount: 0, projectCount: 0, missingCount: 0 }
      }
      this.fetchProject()
    },
    async handleExport () {
      if (!this.project.total) return this.$message.warning('当前筛选条件下无数据可导出')
      this.project.exporting = true
      try {
        const params = { ...this._spanParams(this.project.dateRange) }
        if (this.project.brand) params.brand = this.project.brand
        if (this.project.signerId) params.signerId = this.project.signerId
        if (this.project.keyword.trim()) params.keyword = this.project.keyword.trim()
        const { data } = await this.$http.get('finance/yunyingData/project/export', { params, responseType: 'blob' })
        if (data.type === 'application/json') {
          const err = JSON.parse(await data.text())
          return this.$message.error((err.meta && err.meta.msg) || '导出失败')
        }
        this._downloadBlob(data, `运营数据-项目经营汇总_${this._dateTag(this.project.dateRange)}.xlsx`)
      } catch (error) {
        this.$message.error('导出失败')
      } finally {
        this.project.exporting = false
      }
    },
    _downloadBlob (blob, filename) {
      const url = window.URL.createObjectURL(new Blob([blob]))
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(url)
    },

    /**
     * 签约人下拉选项
     */
    async loadOptions () {
      try {
        const { data: res } = await this.$http.get('finance/yunyingData/filter/options')
        if (res.meta.status !== 200) return this.$message.error(res.meta.msg)
        this.signers = res.data.signers || []
      } catch (error) {
        this.$message.error('获取签约人列表失败')
      }
    },

    /* ===== Tab2 门店收入明细 ===== */
    onStoreFilterChange () {
      this.store.pagenum = 1
      this.fetchStore()
    },
    async fetchStore () {
      this.store.loading = true
      try {
        const params = {
          pagenum: this.store.pagenum,
          pagesize: this.store.pagesize,
          ...this._spanParams(this.store.dateRange)
        }
        if (this.store.keyword.trim()) params.keyword = this.store.keyword.trim()
        if (this.store.platform) params.platform = this.store.platform
        if (this.store.brand) params.brand = this.store.brand
        const { data: res } = await this.$http.get('finance/yunyingData/store/list', { params })
        if (res.meta.status !== 200) return this.$message.error(res.meta.msg)
        this.store.list = res.data.list || []
        this.store.total = res.data.total || 0
      } catch (error) {
        this.$message.error('获取门店收入明细失败')
      } finally {
        this.store.loading = false
      }
    },
    resetStoreFilter () {
      this.store = {
        dateRange: this._defaultMonthRange(),
        keyword: '',
        platform: '',
        brand: '',
        pagenum: 1,
        pagesize: 20,
        list: [],
        total: 0,
        loading: false
      }
      this.fetchStore()
    },

    /* ===== Tab3 品牌月度汇总 ===== */
    async fetchBrandMonthly () {
      this.brand.loading = true
      try {
        const { data: res } = await this.$http.get('finance/yunyingData/brand/monthly', { params: this._spanParams(this.brand.dateRange) })
        if (res.meta.status !== 200) return this.$message.error(res.meta.msg)
        this.brand.list = res.data.list || []
        this.brand.months = res.data.months || []
      } catch (error) {
        this.$message.error('获取品牌月度汇总失败')
      } finally {
        this.brand.loading = false
      }
    },
    /**
     * Tab3 合计行：品牌列显示"合计"，月份/合计/应抽列均汇总
     */
    brandSummary ({ columns, data }) {
      const monthCount = this.brand.months.length
      return columns.map((col, index) => {
        if (index === 0) return '合计'
        if (index === monthCount + 1) return this.fmtMoney(data.reduce((s, r) => s + (r.totalAmount || 0), 0))
        if (index === monthCount + 2) return this.fmtMoney(data.reduce((s, r) => s + (r.totalDueAmount || 0), 0))
        const mi = index - 1
        return this.fmtMoney(data.reduce((s, r) => s + ((r.months[mi] && r.months[mi].amount) || 0), 0))
      })
    }
  }
}
</script>

<style lang="less" scoped>
.container {
  .main-card {
    margin-top: 15px;
  }
}

.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
}
.card-header {
  font-size: 16px;
  font-weight: bold;
}
.header-summary {
  font-size: 12px;
  font-weight: normal;
  color: #909399;
  margin-left: 12px;
}
.toolbar-tip {
  color: #909399;
  font-size: 12px;
}

/* 指标卡 */
.stat-cards {
  display: flex;
  gap: 12px;
  margin-bottom: 12px;
}
.stat-card {
  flex: 1;
  min-width: 0;
  padding: 10px 12px;
  border: 1px solid #EBEEF5;
  border-radius: 4px;
  background: #FAFBFC;
  text-align: center;
}
.stat-card-num {
  font-size: 24px;
  font-weight: bold;
  line-height: 1.2;
  color: #303133;
}
.stat-card.warn .stat-card-num {
  color: #F56C6C;
}
.stat-card-label {
  font-size: 12px;
  color: #606266;
  margin-top: 2px;
}

/* 筛选行 */
.filter-row {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  padding-top: 4px;
  flex-wrap: wrap;
  row-gap: 8px;
  .el-select,
  .el-input,
  .el-date-editor {
    margin-right: 8px;
  }
  .el-button {
    margin-left: 0;
    margin-right: 8px;
  }
}

/* 表格内文字 */
.row-tag {
  margin-left: 4px;
}
.muted-text {
  color: #c0c4cc;
}
.money-text {
  color: #303133;
  font-weight: 500;
}
.money-refund {
  color: #F56C6C;
  font-weight: 500;
}
.ok-text {
  color: #67C23A;
}
.due-text {
  color: #409EFF;
  font-weight: bold;
}

/* 展开行：计算依据 */
.expand-body {
  padding: 4px 12px;
}
.expand-title {
  font-size: 13px;
  font-weight: bold;
  color: #303133;
  margin-bottom: 6px;
}
.expand-line {
  font-size: 12px;
  color: #606266;
  line-height: 22px;
}
.expand-line b {
  color: #303133;
}
.warn-line {
  color: #E6A23C;
}

/* 表格底部口径提示 */
.foot-tip {
  margin-top: 10px;
  font-size: 12px;
  color: #909399;
}

.pager {
  margin-top: 12px;
  text-align: right;
}
</style>
